import React, { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  Link
} from '@mui/material';
import { useForgotLoginApis } from '../../apis/ForgotLoginAPIs';

const ForgotPassword = ({ onBackToLogin }) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const { VerifySecurityCode, RequestSecurityCode, updateForgotPassword } = useForgotLoginApis();

  // Handle cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

const handleUsernameSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setErrorMessage('');
  
  try {
    const data = await RequestSecurityCode(username);
    if (data && data.studentEmail) {  // Only proceed if we got valid data
      setEmail(data.studentEmail);
      setStep(2);
      setCooldown(30); // Set 30-second cooldown after first code send
    } else {
      throw new Error('Email Service is Down');
    }
  } catch (err) {
    setErrorMessage(err.message || 'Username not found. Please try again.');
    setOpenSnackbar(true);
  } finally {
    setLoading(false);
  }
};

  const handleResendCode = async () => {
    setResendLoading(true);
    setErrorMessage('');
    
    try {
      await RequestSecurityCode(username);
      setCooldown(30); // Reset cooldown when resending
      setErrorMessage('New verification code sent!');
      setOpenSnackbar(true);
    } catch (err) {
      setErrorMessage('Failed to resend code. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await VerifySecurityCode(username, parseInt(code, 10)); 
      setUserId(response.userId)
      sessionStorage.setItem('jwt', response.jwtToken)
      if (response.success === 'false') { 
        throw new Error('Invalid verification code');
      }
      
      setStep(3);
    } catch (err) {
      setErrorMessage(err.message);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setOpenSnackbar(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        userId: userId, 
        newPassword
      };

      const success = await updateForgotPassword(userData);
      if (success) {
        setUsername('');
        setEmail('');
        setCode('');
        setUserId('');

        setStep(4);
        setTimeout(() => onBackToLogin(), 4000);
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      setErrorMessage('Failed to reset password. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setNewPassword('');
      setConfirmPassword('');
      setLoading(false);
    }
  };

  return (
    <div>
      {step === 1 && (
        <form onSubmit={handleUsernameSubmit}>
          <Stack spacing={2} alignItems="center" mt={8}>
            <Typography variant="h4" gutterBottom>Reset Password</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your username to receive a verification code
            </Typography>
            
            <TextField
              name="username"
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              sx={{ width: '200px' }} 
            />
            
            <Button 
              variant="outlined" 
              color="primary" 
              type="submit" 
              disabled={loading}
              sx={{ width: '120px' }} 
            >
              {loading ? <CircularProgress size={24} /> : 'Send Code'}
            </Button>
            
            <Link 
              component="button" 
              variant="body2"
              onClick={onBackToLogin}
              disabled={loading}
              sx={{ mt: 1 }}
            >
              Back to Login
            </Link>
          </Stack>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleCodeSubmit}>
          <Stack spacing={2} alignItems="center" mt={8}>
            <Typography variant="h4" gutterBottom>Verify Code</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              We've sent a 5-digit code to {email}
            </Typography>
            
            <TextField
              name="code"
              label="Verification Code"
              value={code}
              onChange={(e) => {
                // only numeric input
                const numericValue = e.target.value.replace(/\D/g, '');
                // Limit to 5 digits
                setCode(numericValue.slice(0, 5));
              }}
              required
              sx={{ width: '160px' }}
              inputProps={{ 
                maxLength: 5,
                inputMode: 'numeric' // Shows numeric keypad on mobile
              }}
            />
            
            <Stack direction="row" spacing={2}>
              <Button 
                variant="outlined" 
                color="primary" 
                type="submit" 
                disabled={loading}
                sx={{ width: '140px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>
              
              <Button 
                variant="text" 
                color="primary" 
                onClick={handleResendCode}
                disabled={resendLoading || cooldown > 0}
                sx={{ width: '140px' }}
              >
                {resendLoading ? (
                  <CircularProgress size={24} />
                ) : cooldown > 0 ? (
                  `Resend (${cooldown}s)`
                ) : (
                  'Resend Code'
                )}
              </Button>
            </Stack>
            
            <Link 
              component="button" 
              variant="body2"
              onClick={() => setStep(1)}
              disabled={loading || resendLoading}
              sx={{ mt: 1 }}
            >
              Back
            </Link>
          </Stack>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handlePasswordReset}>
          <Stack spacing={2} alignItems="center" mt={8}>
            <Typography variant="h4" gutterBottom>New Password</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Create your new password
            </Typography>
            
            <TextField
              type="password"
              name="newPassword"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ width: '280px' }}
            />
            
            <TextField
              type="password"
              name="confirmPassword"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ width: '280px' }}
            />
            
            <Button 
              variant="outlined" 
              color="primary" 
              type="submit" 
              disabled={loading}
              sx={{ width: '160px' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>
            
            <Link 
              component="button" 
              variant="body2"
              onClick={onBackToLogin}
              disabled={loading}
              sx={{ mt: 1 }}
            >
              Back to Login
            </Link>
          </Stack>
        </form>
      )}

      {step === 4 && (
        <Stack spacing={2} alignItems="center" mt={8}>
          <Typography variant="h4" gutterBottom>Password Updated!</Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Your password has been successfully updated.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll be redirected to the login page shortly...
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
        </Stack>
      )}
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default ForgotPassword;